import { useEffect, useRef, useState } from "react";

const normalizeOptions = (options = []) =>
  options
    .map((opt, idx) => {
      if (opt == null) return null;
      if (typeof opt === "object") {
        const value = opt.value ?? idx;
        const label = opt.label ?? String(value);
        return { ...opt, value, label };
      }
      return { value: opt, label: String(opt) };
    })
    .filter(Boolean);

const OrganizerPillSelect = ({
  placeholder = "Seleccionar",
  value,
  onChange,
  options = [],
  disabled = false,
  className = "",
  // Adds a "Buscar..." box that filters options by label (for long lists such as cities).
  searchable = false,
  // Optional accessible naming: `id` goes on the trigger and `labelledBy` is the id of an external
  // visible label. The trigger is named "<label> <selected value>" so the current choice is still read.
  id,
  labelledBy,
}) => {
  const normalizedOptions = normalizeOptions(options);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const selected = normalizedOptions.find((opt) => String(opt.value) === String(value));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleSelect = (val) => {
    if (disabled) return;
    if (typeof onChange === "function") onChange(val);
    setOpen(false);
    setQuery("");
  };

  const visibleOptions = searchable && query.trim()
    ? normalizedOptions.filter((opt) => String(opt.label).toLowerCase().includes(query.trim().toLowerCase()))
    : normalizedOptions;

  return (
    <div
      className={`organizer-pill-select ${disabled ? "is-disabled" : ""} ${open ? "is-open" : ""} ${className}`}
      ref={containerRef}
    >
      <button
        type="button"
        id={id}
        className="organizer-pill-trigger"
        aria-labelledby={labelledBy && id ? `${labelledBy} ${id}` : undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
      >
        <span className="pill-label">{(selected?.label || placeholder)}</span>
        <span className="pill-arrow" aria-hidden="true" />
      </button>
      {open && (
        <div className="organizer-pill-menu" role="listbox" aria-labelledby={labelledBy}>
          {searchable && (
            <input
              type="text"
              className="organizer-pill-search"
              placeholder="Buscar..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          )}
          {visibleOptions.map((opt) => {
            const active = String(opt.value) === String(value);
            return (
              <button
                type="button"
                key={opt.value ?? opt.label}
                className={`organizer-pill-option ${active ? "is-active" : ""}`}
                onClick={() => handleSelect(opt.value)}
                role="option"
                aria-selected={active}
                disabled={opt.disabled}
              >
                {opt.label ?? opt.value}
              </button>
            );
          })}
          {visibleOptions.length === 0 && <div className="organizer-pill-empty">Sin opciones</div>}
        </div>
      )}
    </div>
  );
};

export default OrganizerPillSelect;
